/**
 * Minimal ZIP reader — the other half of `zip.ts`.
 *
 * markbird exports the library as a `.zip` but could never read one back, so
 * the export was a one-way door. This reads what `zipStore` writes (stored
 * entries) and what every OS and most tools produce (deflate), which covers
 * "export from one browser, import into another" and "zip a folder of notes".
 *
 * It reads the central directory rather than scanning local headers: entries
 * written by streaming tools carry their sizes only in the central directory,
 * and scanning local headers would misread them.
 *
 * The limits are not decoration. A 1 MB zip can inflate to gigabytes, and this
 * runs on the main thread of an editor; a crafted archive should fail with a
 * message, not freeze the tab.
 */

export const MAX_ENTRIES = 5000;
export const MAX_TOTAL_BYTES = 200 * 1024 * 1024;

export class ZipError extends Error {
  constructor(
    message: string,
    readonly code: 'invalid' | 'too_large' | 'unsupported',
  ) {
    super(message);
    this.name = 'ZipError';
  }
}

export interface UnzippedFile {
  /** Path inside the archive, `/`-separated. */
  path: string;
  data: Uint8Array<ArrayBuffer>;
}

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;

async function inflateRaw(
  bytes: Uint8Array<ArrayBuffer>,
  expected: number,
): Promise<Uint8Array<ArrayBuffer>> {
  const stream = new DecompressionStream('deflate-raw');
  const writer = stream.writable.getWriter();
  const written = writer.write(bytes).then(() => writer.close());
  written.catch(() => {});

  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.length;
    // Stop as soon as an entry inflates past what its header declared: that is
    // either corruption or a bomb, and reading on would only waste memory.
    if (total > expected) {
      await reader.cancel();
      throw new ZipError('An entry is larger than the archive says it is.', 'invalid');
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/** Read every file in a ZIP archive. Directories are skipped. */
export async function unzip(buffer: ArrayBuffer): Promise<UnzippedFile[]> {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  // The end-of-central-directory record sits at the end, followed by an
  // optional comment of up to 65535 bytes.
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new ZipError('This is not a ZIP archive.', 'invalid');

  const count = view.getUint16(eocd + 10, true);
  let cursor = view.getUint32(eocd + 16, true);
  if (count > MAX_ENTRIES) {
    throw new ZipError(`The archive has more than ${MAX_ENTRIES} entries.`, 'too_large');
  }

  const decoder = new TextDecoder();
  const files: UnzippedFile[] = [];
  let totalBytes = 0;

  for (let i = 0; i < count; i++) {
    if (cursor + 46 > bytes.length || view.getUint32(cursor, true) !== CENTRAL_SIGNATURE) {
      throw new ZipError('The archive is damaged.', 'invalid');
    }
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const size = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    const path = decoder.decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
    cursor += 46 + nameLength + extraLength + commentLength;

    if (path.endsWith('/')) continue;
    if (flags & 0x1) throw new ZipError('Encrypted archives are not supported.', 'unsupported');

    totalBytes += size;
    if (totalBytes > MAX_TOTAL_BYTES) {
      throw new ZipError('The archive is too large to import.', 'too_large');
    }

    if (view.getUint32(localOffset, true) !== LOCAL_SIGNATURE) {
      throw new ZipError('The archive is damaged.', 'invalid');
    }
    // Local name/extra lengths can differ from the central directory's copy.
    const dataStart =
      localOffset +
      30 +
      view.getUint16(localOffset + 26, true) +
      view.getUint16(localOffset + 28, true);
    const raw = bytes.slice(dataStart, dataStart + compressedSize);

    if (method === 0) files.push({ path, data: raw });
    else if (method === 8) files.push({ path, data: await inflateRaw(raw, size) });
    else throw new ZipError('The archive uses an unsupported compression method.', 'unsupported');
  }

  return files;
}
